import React from "react";
import "./ckeditor.css";
import { useAppSelector } from "../store/hooks";
import { selectSections } from "../store/slices/configSlice";
import "../vendor/perfect-scrollbar.css";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
    DecoupledEditor,
    Alignment,
    Autoformat,
    Base64UploadAdapter,
    Bold,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    ImageBlock,
    PictureEditing,
    ImageInsert,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    ListProperties,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    Strikethrough,
    Subscript,
    Superscript,
    Table,
    TableCellProperties,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline,
    WordCount,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

import PlaceholderEditing from "./PlaceholderEditing";
import SectionHolderEditing from "./SectionHolderEditing";

// CKEditor v46 collapsed every plugin into a single `ckeditor5` package.
// Premium license key drives premium features; fall back to GPL otherwise.
const LICENSE_KEY = import.meta.env.VITE_CKEDITOR_LICENSE_KEY || "GPL";

const commonPlugins = [
    Alignment,
    Autoformat,
    Base64UploadAdapter,
    Bold,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    Image,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    ImageBlock,
    PictureEditing,
    ImageInsert,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    ListProperties,
    PageBreak,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    Strikethrough,
    Subscript,
    Superscript,
    Table,
    TableCellProperties,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline,
    WordCount,
    PlaceholderEditing,
];

const toolbar = [
    "heading",
    "|",
    "fontSize",
    "fontFamily",
    "|",
    "fontColor",
    "fontBackgroundColor",
    "|",
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "|",
    "alignment",
    "|",
    "numberedList",
    "bulletedList",
    "|",
    "outdent",
    "indent",
    "|",
    "todoList",
    "link",
    "imageUpload",
    "insertTable",
    "|",
    "undo",
    "redo",
    "horizontalLine",
    "pageBreak",
    "superscript",
    "subscript",
    "removeFormat",
];

const MyCKEditor = ({ setEditor, type, onChange, editor }) => {

    const placeholders = useAppSelector(state => state.configReducer.placeholders);
    const sections = useAppSelector(selectSections);

    const colors = [
        { color: "#000000", label: "Black" },
        { color: "#4D4D4D", label: "Dim grey" },
        { color: "#999999", label: "Grey" },
        { color: "#E6E6E6", label: "Light grey" },
        { color: "#FFFFFF", label: "White" },
        { color: "#E64C4C", label: "Red" },
        { color: "#E6994C", label: "Orange" },
        { color: "#E6E64C", label: "Yellow" },
        { color: "#99E64C", label: "Light Green" },
        { color: "#4CE64C", label: "Green" },
        { color: "#4CE699", label: "Aquamarine" },
        { color: "#4CE6E6", label: "Turquoise" },
        { color: "#4C99E6", label: "Light blue" },
        { color: "#4C4CE6", label: "Blue" },
        { color: "#994CE6", label: "Purple" },
    ];

    const plugins = [...commonPlugins];

    switch (type) {
        case "template":
            plugins.push(SectionHolderEditing);
            break;
        case "section":
        case "document":
        default:
            break;
    }

    const editorConfiguration = {
        plugins,
        licenseKey: LICENSE_KEY,
        toolbar,
        table: {
            contentToolbar: [
                "tableColumn",
                "tableRow",
                "mergeTableCells",
                "tableProperties",
                "tableCellProperties",
            ],
        },
        image: {
            styles: ["alignCenter", "alignLeft", "alignRight"],
            toolbar: [
                "imageStyle:inline",
                "imageStyle:wrapText",
                "imageStyle:breakText",
                "imageStyle:side",
                "|",
                "resizeImage",
            ],
            resizeOptions: [
                { name: "resizeImage:original", value: null, label: "Original" },
                { name: "resizeImage:10", value: "10", label: "10%" },
                { name: "resizeImage:20", value: "20", label: "20%" },
                { name: "resizeImage:47", value: "47", label: "47%" },
            ],
        },
        fontSize: {
            options: [
                { title: "9", model: "9pt" },
                { title: "9.5", model: "9.5pt" },
                { title: "10", model: "10pt" },
                { title: "10.5", model: "10.5pt" },
                { title: "11", model: "11pt" },
                "default",
                { title: "12", model: "12pt" },
                { title: "14", model: "14pt" },
                { title: "16", model: "16pt" },
                { title: "18", model: "18pt" },
                { title: "20", model: "20pt" },
                { title: "22", model: "22pt" },
                { title: "24", model: "24pt" },
                { title: "26", model: "26pt" },
                { title: "28", model: "28pt" },
                { title: "36", model: "36pt" },
            ],
        },
        fontColor: { colors },
        fontBackgroundColor: { colors },
        list: {
            properties: { styles: true, startIndex: true, reversed: true },
        },
        htmlSupport: {
            allow: [
                {
                    name: "section",
                    styles: true,
                    classes: ["sectionholder"],
                    attributes: { "data-key": true },
                },
            ],
        },
    };

    if (placeholders.length === 0 || sections.length === 0) return null;

    return (
        <div className="document-editor">
            <div className="document-editor__toolbar"></div>
            <div className="document-editor__editable-container">
                <CKEditor
                    editor={DecoupledEditor}
                    config={editorConfiguration}
                    onReady={(editor) => {
                        const toolbarContainer = document.querySelector(".document-editor__toolbar");
                        toolbarContainer.appendChild(editor.ui.view.toolbar.element);
                        setEditor && setEditor(editor);
                    }}
                    onError={(error, { willEditorRestart }) => {
                        if (willEditorRestart) {
                            editor.ui.view.toolbar.element.remove();
                        }
                        Object.getOwnPropertyNames(error).forEach((property) => {
                            const descriptor = Object.getOwnPropertyDescriptor(error, property);
                            console.log("error", property, descriptor);
                        });
                    }}
                    onBlur={(event, editor) => {
                        const data = editor.getData();
                        onChange && onChange(data);
                    }}
                />
            </div>
        </div>
    );
};

export default MyCKEditor;
